/**
 * Proactive sections, pure logic.
 *
 * A page section opts in with three attributes on its wrapper element (emitted
 * server-side, e.g. on a `core/group`):
 *
 *   data-dhsv-chat-section="services"
 *   data-dhsv-chat-question="Welche Leistung passt zu Ihrem Vorhaben?"
 *   data-dhsv-chat-pills='["Leistungen im Überblick","Was kostet das?"]'
 *
 * While the visitor reads that section, the host's teaser shows the question
 * and the pills instead of its default text. This module parses and sanitises
 * the attributes and picks the active section; the DOM wiring (observer,
 * teaser) lives in `iframe-host.ts`.
 */

export const SECTION_ATTRIBUTE = 'data-dhsv-chat-section';
export const QUESTION_ATTRIBUTE = 'data-dhsv-chat-question';
export const PILLS_ATTRIBUTE = 'data-dhsv-chat-pills';
/** Elements that may become a section; the rest is checked by the parser. */
export const SECTION_SELECTOR = `[${ SECTION_ATTRIBUTE }][${ QUESTION_ATTRIBUTE }]`;

export const MAX_QUESTION_LENGTH = 90;
export const MAX_PILL_LENGTH = 26;
export const MAX_PILLS = 3;
/** Section ids go into tracking pushes and the chat context: ids only. */
export const SECTION_ID_PATTERN = /^[A-Za-z0-9_.-]{1,64}$/;

/**
 * The reading band: from 35 % to 55 % of the viewport height
 * (`04-proaktive-ebene.md` §2).
 */
export const SECTION_ROOT_MARGIN = '-35% 0px -45% 0px';
/** No more than one text swap in this window, so fast scrolling does not flicker. */
export const MIN_SWAP_INTERVAL_MS = 4000;

export type ProactiveSection = {
	id: string;
	question: string;
	pills: string[];
};

export type RawSectionAttributes = {
	section: string | null;
	question: string | null;
	pills: string | null;
};

/**
 * A visitor-facing text: whitespace collapsed, trimmed, cut at `max` code
 * points; anything but a string is empty. Shared with the Schnellfragen
 * (`quick-questions.ts`).
 *
 * @param {unknown} value Raw value.
 * @param {number}  max   Maximum length in code points.
 */
export function cleanText( value: unknown, max: number ): string {
	if ( typeof value !== 'string' ) {
		return '';
	}
	return Array.from( value.replace( /\s+/g, ' ' ).trim() )
		.slice( 0, max )
		.join( '' )
		.trim();
}

/**
 * The pills of a section: a JSON array of strings, at most three non-empty
 * ones, each cut to 26 characters. Anything else yields no pills.
 *
 * @param {string|null} raw The attribute value.
 */
export function parsePills( raw: string | null ): string[] {
	if ( ! raw ) {
		return [];
	}
	let parsed: unknown;
	try {
		parsed = JSON.parse( raw );
	} catch {
		return [];
	}
	if ( ! Array.isArray( parsed ) ) {
		return [];
	}
	const pills: string[] = [];
	for ( const item of parsed ) {
		const text = cleanText( item, MAX_PILL_LENGTH );
		if ( text !== '' ) {
			pills.push( text );
		}
		if ( pills.length === MAX_PILLS ) {
			break;
		}
	}
	return pills;
}

/**
 * A section from its attributes, or `null` when the id or the question is
 * missing or the id is not a plain id.
 *
 * @param {RawSectionAttributes} raw The three attribute values.
 */
export function parseSectionAttributes(
	raw: RawSectionAttributes
): ProactiveSection | null {
	const id = ( raw.section ?? '' ).trim();
	if ( ! SECTION_ID_PATTERN.test( id ) ) {
		return null;
	}
	const question = cleanText( raw.question, MAX_QUESTION_LENGTH );
	if ( question === '' ) {
		return null;
	}
	return { id, question, pills: parsePills( raw.pills ) };
}

/**
 * Read a section from an element's attributes.
 *
 * @param {Element} element The section wrapper.
 */
export function readSection( element: Element ): ProactiveSection | null {
	return parseSectionAttributes( {
		section: element.getAttribute( SECTION_ATTRIBUTE ),
		question: element.getAttribute( QUESTION_ATTRIBUTE ),
		pills: element.getAttribute( PILLS_ATTRIBUTE ),
	} );
}

/**
 * The active section: the last one in document order that intersects the
 * reading band, or `null` when none does.
 *
 * @param {Array}    ordered      Candidates in document order.
 * @param {Function} intersecting Whether a candidate is in the band.
 */
export function pickActiveSection< T >(
	ordered: readonly T[],
	intersecting: ( item: T ) => boolean
): T | null {
	for ( let i = ordered.length - 1; i >= 0; i-- ) {
		const item = ordered[ i ] as T;
		if ( intersecting( item ) ) {
			return item;
		}
	}
	return null;
}

/**
 * Two sections render the same: same id, question and pills.
 *
 * @param {ProactiveSection|null} a One section.
 * @param {ProactiveSection|null} b The other.
 */
export function sameSection(
	a: ProactiveSection | null,
	b: ProactiveSection | null
): boolean {
	if ( a === null || b === null ) {
		return a === b;
	}
	return (
		a.id === b.id &&
		a.question === b.question &&
		a.pills.length === b.pills.length &&
		a.pills.every( ( pill, i ) => pill === b.pills[ i ] )
	);
}

/**
 * How long a swap has to wait so that swaps stay `MIN_SWAP_INTERVAL_MS` apart.
 *
 * @param {number|null} lastSwapAt When the content last changed, or `null`.
 * @param {number}      now        `Date.now()`.
 */
export function swapDelay( lastSwapAt: number | null, now: number ): number {
	if ( lastSwapAt === null ) {
		return 0;
	}
	return Math.max( 0, lastSwapAt + MIN_SWAP_INTERVAL_MS - now );
}
